import type { LatLng } from "@components/store-locator/humanize-distance";
import { distanceInKm, humanizeDistance } from "@components/store-locator/humanize-distance";
import type { PlpStoreLookup } from "@lib/utils/plp/normalizeStoresForPlp";
import { STORES_FACET } from "@lib/constants/filtersConstants";

import { normalizeStoreId } from "./storeSizesFilter";
import { resolvePlpStoreDisplayName } from "./plpStoreDisplayName";

type FilterOption = { value: string; label: string };

export const isStoresFacet = (facetName?: string) => facetName === STORES_FACET;

export type StoreLookupMaps = {
    nameById: Map<string, string>;
    coordsById: Map<string, LatLng>;
};

export const createStoreLookupMaps = (stores: PlpStoreLookup[]): StoreLookupMaps =>
    buildStoreLookupMaps(stores);

const resolveStoreLabel = (storeId: string, nameById: Map<string, string>, fallback: string) =>
    resolvePlpStoreDisplayName(
        storeId,
        nameById.get(storeId) ?? nameById.get(normalizeStoreId(storeId)) ?? fallback
    );

const buildStoreLookupMaps = (stores: PlpStoreLookup[]): StoreLookupMaps => {
    const nameById = new Map<string, string>();
    const coordsById = new Map<string, LatLng>();

    for (const { storeId, name, countryCode, latitude, longitude } of stores) {
        nameById.set(storeId, name);
        if (latitude != null && longitude != null) {
            coordsById.set(storeId, { latitude, longitude });
        }

        if (countryCode) {
            const countryCodeUpper = countryCode.toUpperCase();

            nameById.set(`${countryCode}-${storeId}`, name);
            nameById.set(`${countryCodeUpper}-${storeId}`, name);

            if (latitude != null && longitude != null) {
                const coords = { latitude, longitude };
                coordsById.set(`${countryCode}-${storeId}`, coords);
                coordsById.set(`${countryCodeUpper}-${storeId}`, coords);
            }
        }
    }

    return { nameById, coordsById };
};

const getStoreCoords = (facetValue: string, coordsById: Map<string, LatLng>) =>
    coordsById.get(facetValue) ?? coordsById.get(normalizeStoreId(facetValue));

type StoreFacetPresentationConfig = {
    isAppendDistance: boolean;
    locale: string;
    unitSystem: "metric" | "imperial";
};

export const getStoreDisplayName = ({
    storeId,
    stores,
    userCoords,
    locale,
    unitSystem,
    lookupMaps,
}: {
    storeId: string;
    stores?: PlpStoreLookup[];
    userCoords: LatLng | null;
    locale: string;
    unitSystem: "metric" | "imperial";
    lookupMaps?: StoreLookupMaps;
}): string => {
    if (!stores?.length) return resolvePlpStoreDisplayName(storeId);
    const { nameById, coordsById } = lookupMaps ?? buildStoreLookupMaps(stores);
    const name = resolveStoreLabel(storeId, nameById, storeId);

    if (!userCoords) return name;

    const storeCoords = getStoreCoords(storeId, coordsById);
    if (!storeCoords) return name;

    const distanceLabel = humanizeDistance(userCoords, storeCoords, locale, unitSystem);
    return `${name} (${distanceLabel})`;
};

/** Map Algolia store IDs (e.g. PL-0001048083) to CommerceTools store names for filter labels. */
export const withStoreNames = <T extends FilterOption>(
    options: T[],
    stores?: PlpStoreLookup[],
    lookupMaps?: StoreLookupMaps
): T[] => {
    if (!stores?.length) {
        return options.map((option) => ({
            ...option,
            label: resolvePlpStoreDisplayName(option.value, option.label),
        }));
    }

    const { nameById } = lookupMaps ?? buildStoreLookupMaps(stores);

    return options.map((option) => ({
        ...option,
        label: resolveStoreLabel(option.value, nameById, option.label),
    }));
};

/** Sort store facet options closest-first when location is available, otherwise A–Z by label. */
export const sortStoreFacetOptions = <T extends FilterOption>({
    options,
    userCoords,
    lookupMaps,
    isAppendDistance,
    locale,
    unitSystem,
}: {
    options: T[];
    userCoords: LatLng | null | undefined;
    lookupMaps: StoreLookupMaps;
} & StoreFacetPresentationConfig): T[] => {
    if (!userCoords) {
        return options.toSorted((a, b) => a.label.localeCompare(b.label, locale));
    }

    const { coordsById } = lookupMaps;

    const withDistance = options.map((option) => {
        const storeCoords = getStoreCoords(option.value, coordsById);
        const distanceKm = storeCoords ? distanceInKm(userCoords, storeCoords) : null;

        let label = option.label;
        if (isAppendDistance && storeCoords) {
            const distanceLabel = humanizeDistance(userCoords, storeCoords, locale, unitSystem);
            label = `${option.label} (${distanceLabel})`;
        }

        return { option: { ...option, label }, distanceKm };
    });

    return withDistance
        .toSorted((a, b) => {
            if (a.distanceKm == null && b.distanceKm == null) {
                return a.option.label.localeCompare(b.option.label, locale);
            }
            if (a.distanceKm == null) return 1;
            if (b.distanceKm == null) return -1;
            if (a.distanceKm === b.distanceKm) {
                return a.option.label.localeCompare(b.option.label, locale);
            }
            return a.distanceKm - b.distanceKm;
        })
        .map(({ option }) => option);
};

/** Map store names, then sort by distance (with location) or alphabetically (without). */
export const prepareStoreFacetOptions = <T extends FilterOption>(
    options: T[],
    stores: PlpStoreLookup[] | undefined,
    userCoords: LatLng | null | undefined,
    config: StoreFacetPresentationConfig
): T[] => {
    if (!stores?.length) {
        return options
            .map((option) => ({
                ...option,
                label: resolvePlpStoreDisplayName(option.value, option.label),
            }))
            .toSorted((a, b) => a.label.localeCompare(b.label, config.locale));
    }

    const lookupMaps = buildStoreLookupMaps(stores);
    return sortStoreFacetOptions({
        options: withStoreNames(options, stores, lookupMaps),
        userCoords,
        lookupMaps,
        ...config,
    });
};
